import { useLazyQuery } from "@apollo/client";
import { useCallback } from "react";

import { GET_USER_CLIP_ELIGIBILITY } from "MVVM/GraphQL/quickEdit";
import { analytics } from "MVVM/Analytics";

export interface IClipEligibilityError {
  field: string;
  code: string;
  message: string;
}

export interface IClipEligibilityResponse {
  errors?: IClipEligibilityError[];
  success: boolean;
  reason?: string;
  isEligible: boolean;
  canCreateClipAt?: string;
}

export interface IClipEligibilityData {
  getUserClipEligibility: IClipEligibilityResponse;
}

export const useClipEligibility = () => {
  const [checkEligibility] = useLazyQuery<IClipEligibilityData>(
    GET_USER_CLIP_ELIGIBILITY,
    {
      fetchPolicy: "network-only",
    },
  );

  const checkUserClipEligibility = useCallback(
    async (userId: string, action?: "create" | "remix") => {
      try {
        const result = await checkEligibility({
          variables: { userId },
        });

        const eligibility = result.data?.getUserClipEligibility;

        if (!eligibility) return null;

        const eventName = eligibility.isEligible
          ? "Clip Eligibility Check Passed"
          : "Clip Eligibility Check Blocked";

        const eventProperties = {
          action,
          reason: eligibility.reason,
          canCreateClipAt: eligibility.canCreateClipAt,
        };

        analytics.track(eventName, eventProperties);

        return eligibility;
      } catch (err) {
        analytics.track("Clip Eligibility Check Error", {
          error: err instanceof Error ? err.message : "Unknown error",
          action,
        });
        return null;
      }
    },
    [checkEligibility],
  );

  return {
    checkUserClipEligibility,
  };
};
